// seo-tools.js

/**
 * SEO Tools Class
 * 
 * This class contains methods to enhance SEO functionalities,
 * including automatic redirection based on user language preferences.
 */
class SeoTools {
  langList = ['fi', 'en']; // list of languages/locations to redirect
  defaultLang = 'en';  // default language/location code
  debug = false; // set to true to enable debug logging
  whitelistUrls = [
    '', // familon.com root
    '/', // familon.com root with trailing slash
    '/fi', // familon.com/fi
  ] // list of URL paths where redirection should occur; empty array means all URLs

  /**
   * The constructor is executed immediately when a new instance of the class is created.
   * It serves as the primary initialization point for the module.
   */
  constructor() {
    if(this.debug) {
      console.log('SeoTools class instance created'); 
    }

    // Call the main initialization method.
    this.init();
  }

  /**
   * The main initialization method. 
   * All primary setup and initial tool execution logic should reside here.
   */
  init() {
    if(this.debug) {
      console.log('SeoTools init() method called'); 
    }

    // Execute the redirect logic.
    this.redirect();
  }

  /**
   * Redirects the user based on their language settings.
   *
   * Example redirects:
   *   - example.com      -> example.com/fi  (if cookie is 'fi')
   *   - example.com/fi   -> example.com/fi (no redirect)
   *
   *
   * NOTE: Hopefully Shopify will fix this issue natively in the future.
   *
   * --- Shopify Support Chat Summary ---
   * Janir discussed with Shopify support about automatic country/language redirection in theme settings:
   *
   * "When you activate automatic redirection, customers see the storefront matching their location preferences.
   * The behavior where automatic language redirection only works on the first visit (and then stops after the _shopify_essential cookie is set) is expected.
   * Shopify's system redirects customers based on IP and browser language, but this typically happens just once within a 14-day period or until cookies are cleared.
   * After the _shopify_essential cookie is set (indicating essential cookies are accepted), Shopify stops redirecting to ensure a smooth experience.
   * This cookie remembers the user's choice or that a redirect has occurred, preventing repeated redirects."
   *
   * Regarding domain redirection to a specific market/language version:
   * "This functionality is very helpful for merchants! Similar requests have been submitted to our developers.
   * I can include your store in the existing request so you'll benefit once it's implemented in future updates."
   *
   * @returns {void}
   */
  redirect()  {
    // check is current URL is in whitelist
    const currentPath = window.location.pathname.replace(/\/+$/, ''); // remove trailing slash
    
    // check if whitelist is not empty
    if(this.whitelistUrls.length > 0) {
      if(this.whitelistUrls.includes(currentPath)) {
        if(this.debug) {
          console.log('Current path is whitelisted:', currentPath);
        }
      } else {
        if(this.debug) {
          console.log('Current path is NOT whitelisted:', currentPath);
        }
        return;
      }
    }

    // get language/location from cookie and user changed location from localStorage
    const cookieLoc = this.parseCookiesLang('localization');
    const userChangedLocation = window.localStorage.getItem('userChangedLocation');

    // determine if redirect is needed and get target language/location
    const redirectTarget = this.getRedirectTarget(cookieLoc, userChangedLocation);

    if(this.debug) {
      console.log('User changed location:', userChangedLocation);
      console.log('Language from cookie:', cookieLoc);
      console.log('Redirect target:', redirectTarget);
    }

    // check if redirect is needed
    if(redirectTarget) {
      // build the new URL for redirection
      let newURL = '';

      // check if redirecting to default language
      if (redirectTarget === this.defaultLang) {
        // for default language, redirect to root (no language code in URL)
        if(this.debug) {
          console.log(`Redirecting to root for default language: ${this.defaultLang}`);
        }
        // this removes the language code from the URL path
        newURL = window.location.origin + window.location.pathname.replace(
          new RegExp(`^/(${this.langList.join('|')})(/|$)`),
          '/'
        ) + window.location.search;
      }
      // redirecting to a non-default language aka specific language code in URL
      else {
        if(this.debug) {
          console.log(`Redirecting to /${redirectTarget} for language: ${redirectTarget}`);
        }
        // this adds the language code to the URL path
        newURL = window.location.origin + '/' + redirectTarget + window.location.pathname + window.location.search;
      }

      if(this.debug) {
        console.log('OLD URL:', window.location.href);
        console.log('NEW URL:', newURL);
      }

      // perform the redirect
      if(!this.debug) {
        window.location.replace(newURL);
        return;
      }

      // In debug mode, do not perform the actual redirect
      console.error('SEO Tools Debug Mode is ON - No redirect performed.');
    }
  }

  /**
   * Determines if a redirect is necessary based on cookie and user settings.
   *
   * @param {string} cookieLoc - The language/location code from the cookie.
   * @param {string} userChangedLocation - The language/location code set by the user.
   * @returns {string|boolean} - Returns the target language code if redirect is needed, otherwise false.
   * 
   * PRIORITY LOGIC:
   * 1. User changed location (localStorage) has highest priority
   * 2. Cookie language check has lowest priority
   * 
   * LOCIC:
   * 1. If userChangedLocation is set and not in redirect list -> no redirect
   * 2. If userChangedLocation matches URL language -> no redirect
   * 3. If userChangedLocation is in redirect list -> redirect to that location
   * 4. If URL already has correct language -> no redirect
   * 5. If cookie language is in redirect list -> redirect to that location
   * 6. Otherwise, no redirect needed
   */
  getRedirectTarget(cookieLoc = '', userChangedLocation = '') {
    // get current language from URL path
    const getLanguageFromURL = this.parseURLLang();

    if(this.debug) {
      console.log('Language from URL:', getLanguageFromURL);
      console.log('Checking if redirect is needed...');
    }

    // Need to have redirect list and cookie set
    if (this.langList.length === 0 || !cookieLoc) {
      if(this.debug) {
        console.log('No redirect list or cookie set.');
      }
      return false;
    }

    // Check user changed location first
    // this is prioritized over cookie and URL checks

    // user has changed location and it's not in redirect list -> no redirect
    if (userChangedLocation && !this.langList.includes(userChangedLocation.toLowerCase())) {
      if(this.debug) {
        console.log('User has manually changed location to:', userChangedLocation, 'No redirect needed.');
      }
      return false;
    } 
    // user has changed location and it matches URL -> no redirect
    else if (userChangedLocation && getLanguageFromURL && userChangedLocation.toLowerCase() === getLanguageFromURL.toLowerCase()) {
      if(this.debug) {
        console.log('User has manually changed location to:', userChangedLocation, 'and URL matches. No redirect needed.');
      }
      return false;
    } 
    // user has changed location and it's in redirect list -> redirect to that location
    else if (userChangedLocation && this.langList.includes(userChangedLocation.toLowerCase())) {
      if(this.debug) {
        console.log('User has manually changed location to:', userChangedLocation, 'Redirect needed.');
      }
      return userChangedLocation.toLowerCase();
    }

    // If URL already has the correct language, no redirect needed
    if(getLanguageFromURL && this.langList.includes(getLanguageFromURL.toLowerCase()) && cookieLoc.toLowerCase() === getLanguageFromURL.toLowerCase()) {
      if(this.debug) {
        console.log('URL already has the correct language. No redirect needed.');
      }
      return false;
    }

    // If cookie language is in redirect list, we need to redirect
    if (this.langList.includes(cookieLoc.toLowerCase())) {
      if(this.debug) {
        console.log('Redirect needed based on cookie language.');
      }
      return cookieLoc.toLowerCase();
    }

    // No redirect needed
    if(this.debug) {
      console.log('No redirect needed.');
    }
    return false;
  }

  /**
   * Parses the language code from the URL path.
   * @returns {string} - The language code parsed from the URL path.
   */
  parseURLLang() {
    const pathLang = window.location.pathname.split('/')[1];
    if (pathLang && this.langList.includes(pathLang.toLowerCase())) {
      return pathLang.toLowerCase();
    }
    return this.defaultLang;
  }

  /**
   * Parses the language code from a cookie.
   * @param {string} name - The name of the cookie to parse.
   * @returns {string|null} - The language code or null if not found.
   */
  parseCookiesLang(name) {
    const cookieValue = this.getCookieItem(name);
    if (cookieValue && this.langList.includes(cookieValue.toLowerCase())) {
      return cookieValue.toLowerCase();
    }
    return this.defaultLang;
  }

  /**
   * Retrieves a specific cookie value by name.
   * @param {string} name - The name of the cookie to retrieve.
   * @returns {string|null} - The value of the cookie or null if not found.
   */
  getCookieItem(name) {
    const value = `; ${document.cookie}`;
    // value: "; localization=FI; cart_currency=EUR; ..."
    const parts = value.split(`; ${name}=`);
    // parts: ["", "FI; cart_currency=EUR; ..."]

    if (parts.length > 1) {
      // Return the cookie value, trimming any extra characters
      // parts[1] is right because we split by `; ${name}=`
      return parts[1].split(';').shift().trim();
    }
    return null;
  }
}

// Instantiate the class immediately upon script loading.
// This ensures the constructor and the init() method are executed 
// as soon as the <script> tag is processed by the browser.
new SeoTools();